import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import type { PLOFormOneData } from '../../../types/PLOs/plo_one.type';
import type { GeoLevel } from '../../../types/database';

import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { FormDocumentRow } from '../../../components/Form/FormDocumentRow';
import {
  fetchPloById,
  startReviewPloForm,
  uploadFileService,
} from '../../../services/PloOneService';
import { submitAttachmentAction } from '../../../services/genericFormServices';
import { ActionSidebar, isCurrentUserOrigin } from '../../../components/Form/ActionSideBar';
import { DetailHeader } from '../../../components/Form/DetailHeader';
import { PloDetailFormOneDocument } from './PLODetailFormOneDocument';
import { StageProgression } from '../../../components/Form/StateProgression';
import type { CreateAttachmentDto } from '../../../types/MainType.type';

export interface PLOFormOneDetailViewProps {
  id?: string;
  onBack?: () => void;
}

export function PLOFormOneDetailView({
  id: propId,
  onBack,
}: PLOFormOneDetailViewProps) {
  const navigate = useNavigate();
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const params = useParams<{ id: string }>();

  // Use propId if passed via props, otherwise grab from URL route (/plo-form-one-detail/:id)
  const id = propId || params.id;

  const [isSigning, setIsSigning] = useState(false);
  const [remark, setRemark] = useState('');
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Form State
  const [formData, setFormData] = useState<PLOFormOneData | null>(null);

  // Fetch record from API whenever `id` is available
  useEffect(() => {
      if (!id) return;
  
      let isSubscribed = true;
      setLoading(true);
      setError(null);
  
      fetchPloById(id)
        .then((data: any) => {
          if (isSubscribed) {
            // Normalize nested form values if wrapped inside legalAdviceFormOne property
            const normalizedData: PLOFormOneData = {
              ...data,
              ...(data.ploFormOne || {}),
            };
            setFormData(normalizedData);
          }
        })
        .catch((err: any) => {
          if (isSubscribed) {
            setError(err?.message || 'အချက်အလက်များ ရယူ၍ မရပါ');
          }
        })
        .finally(() => {
          if (isSubscribed) {
            setLoading(false);
          }
        });
  
      return () => {
        isSubscribed = false;
      };
    }, [id]);
  
    // Handle Loading State
    if (loading) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-3 font-sans">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          <p className="text-sm font-medium text-slate-600">
            အချက်အလက်များကို ရယူနေပါသည်...
          </p>
        </div>
      );
    }
  
    // Handle Missing or Error State
    if (!formData) {
      return (
        <div className="p-6 text-center text-rose-600 font-sans">
          {error || 'အချက်အလက် မရှိပါ။ (Form data not found)'}
        </div>
      );
    }

  // Check form editability condition
  const isOriginUser = isCurrentUserOrigin(formData);
  const isEditable = !isOriginUser && (formData.status === 'UNDER_REVIEW' || formData.status === 'DRAFT');

  // Field Handler Methods
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => (prev ? { ...prev, [name]: value } : prev));
  };

  const handleClearDate = (name: keyof PLOFormOneData) => {
    if (!isEditable) return;
    setFormData((prev) => (prev ? { ...prev, [name]: '' } : prev));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (
      isOriginUser ||
      (!isEditable && formData.status !== 'ATTACHED' && formData.status !== 'SUBMITTED') ||
      !e.target.files
    ) return;
    const filesArray = Array.from(e.target.files);
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            attachments: [...(prev.attachments || []), ...filesArray],
          }
        : prev
    );
  };

  const handleRemoveFile = (index: number) => {
    if (
      isOriginUser ||
      (!isEditable &&
      formData.status !== 'ATTACHED' &&
      formData.status !== 'SUBMITTED')
    ) return;
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            attachments: (prev.attachments || []).filter((_, i) => i !== index),
          }
        : prev
    );
  };

  const handleSaveSignature = () => {
    if (!isEditable) return;
    if (sigCanvasRef.current) {
      if (sigCanvasRef.current.isEmpty()) {
        alert('လက်မှတ် အရင်ရေးထိုးပေးပါရန်။');
        return;
      }
      try {
        const canvas = sigCanvasRef.current.getTrimmedCanvas();
        const base64Data = canvas.toDataURL('image/png');
        setFormData((prev) => (prev ? { ...prev, signatureImage: base64Data } : prev));
        setIsSigning(false);
      } catch {
        const rawCanvas = sigCanvasRef.current.getCanvas();
        const base64Data = rawCanvas.toDataURL('image/png');
        setFormData((prev) => (prev ? { ...prev, signatureImage: base64Data } : prev));
        setIsSigning(false);
      }
    }
  };

  const handleClearSignature = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => (prev ? { ...prev, signatureImage: '' } : prev));
  };

  const handleBackNavigation = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  // Workflow API Actions

   const handleStartReviewAction = async () => {
    const targetId = id || formData?.id;
    if (!targetId) return;

    setSaving(true); // 1. Added loading state trigger
    setError(null);
    try {
      await startReviewPloForm(targetId);
      // 2. Updated status to 'UNDER_REVIEW' to match component checks (isEditable)
      setFormData((prev) => (prev ? { ...prev, status: 'UNDER_REVIEW' } : prev));
      setFormData(await fetchPloById(targetId));
      alert('အကြာင်းကြားခြင်း အောင်မြင်ပါသည်');
    } catch (err: any) {
      setError(err?.message || 'စိစစ်ဆောင်ရွက်ရန် မအောင်မြင်ပါ');
    } finally {
      setSaving(false);
    }
  };

  const handleAttachmentAction = async (
    targetGeoLevels: GeoLevel[],
    forwardGeoLevels: GeoLevel[]
  ) => {
    if (isOriginUser) return;
        const targetId = id || formData?.id;
        if (!targetId) {
          setError('အမှုအမှတ် မရှိပါ (Case ID is missing)');
          return;
        }
    
        setSaving(true);
        setError(null);
    
        try {
          const filesToUpload: File[] = [];
    
          // 1. Separate new raw File objects from already uploaded items
          (formData.attachments || []).forEach((item: any) => {
            if (item instanceof File) {
              filesToUpload.push(item);
            }
          });
    
          // 2. Upload new files concurrently
          const newlyUploadedAttachments: CreateAttachmentDto[] =
            await Promise.all(
              filesToUpload.map(async (file) => {
                const uploadRes = await uploadFileService(file);
                return {
                  fileName: file.name,
                  fileUrl: uploadRes.fileUrl,
                  fileSize: file.size,
                  mimeType: file.type,
                  remarks: remark || undefined,
                };
              })
            );
    
          await submitAttachmentAction(
            targetId,
            remark,
            newlyUploadedAttachments,
            targetGeoLevels,
            forwardGeoLevels
          );
    
          setFormData((prev) =>
            prev
              ? {
                  ...prev,
                  status: forwardGeoLevels.length > 0 ? 'SUBMITTED' : 'ATTACHED',
                  attachments: [],
                  targetGeoLevels,
                  forwardedToLevels: forwardGeoLevels.length
                    ? forwardGeoLevels
                    : undefined,
                }
              : prev
          );
          setFormData(await fetchPloById(targetId));
          setRemark('');
          alert('ဖိုင်တွဲ သိမ်းဆည်းခြင်း အောင်မြင်ပါသည်။ (Attached Successfully)');
        } catch (err: any) {
          setError(err?.message || 'ဖိုင်တွဲ သိမ်းဆည်းခြင်း မအောင်မြင်ပါ');
        } finally {
          setSaving(false);
        }
      };

  // const handleApproveAction = async () => {
  //   const targetId = id || formData.id;
  //   if (!targetId) {
  //     setError('အမှုအမှတ် မရှိပါ (Case ID is missing)');
  //     return;
  //   }

  //   setSaving(true);
  //   setError(null);
  //   try {
  //     await approvePloForm(targetId, remark);
  //     setFormData((prev) => (prev ? { ...prev, status: 'APPROVED' } : prev));
  //     alert('အတည်ပြုခြင်း အောင်မြင်ပါသည်။ (Approved Successfully)');
  //   } catch (err: any) {
  //     setError(err?.message || 'အတည်ပြုခြင်း မအောင်မြင်ပါ');
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  // const handleStartReviewAction = async () => {
  //   const targetId = id || formData.id;
  //   if (!targetId) {
  //     setError('အမှုအမှတ် မရှိပါ (Case ID is missing)');
  //     return;
  //   }

  //   try {
  //     setSaving(true);
  //     setError(null);

  //     const updatedData = await startPloReviewStage(targetId, {
  //       remark: 'Started reviewing process',
  //     });

  //     setFormData((prev) =>
  //       prev
  //         ? {
  //             ...prev,
  //             ...updatedData,
  //             status: 'UNDER_REVIEW',
  //           }
  //         : updatedData
  //     );

  //     alert('စိစစ်မှု စတင်လိုက်ပါပြီ။ (Review Started Successfully)');
  //   } catch (err: any) {
  //     setError(err?.message || 'စိစစ်မှု စတင်ရာတွင် အမှားတစ်ခု ဖြစ်ပေါ်ခဲ့သည်။');
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  // const handleRejectAction = async () => {
  //   if (!remark.trim()) {
  //     setError('ငြင်းပယ်ရသည့် အကြောင်းအရင်း / ပြင်ဆင်ရန် မှတ်ချက် ရေးသားပေးပါ။');
  //     return;
  //   }

  //   const targetId = id || formData.id;
  //   if (!targetId) return;

  //   setSaving(true);
  //   setError(null);
  //   try {
  //     await rejectPloForm(targetId, remark);
  //     setFormData((prev) => (prev ? { ...prev, status: 'RETURNED' } : prev));
  //   } catch (err: any) {
  //     setError(err?.message || 'ငြင်းပယ်ခြင်း မအောင်မြင်ပါ');
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  // const handleSaveAction = async () => {
  //   const targetId = id || formData.id || null;

  //   setSaving(true);
  //   setError(null);
  //   try {
  //     const updated = await savePloDraft(targetId, formData);
  //     setFormData(updated);
  //   } catch (err: any) {
  //     setError(err?.message || 'အချက်အလက်များ သိမ်းဆည်းရာတွင် အမှားတစ်ခု ဖြစ်ပေါ်ခဲ့သည်။');
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  // Sub-render Helpers
  const renderDocumentRow = (label: string, name: keyof PLOFormOneData) => (
    <FormDocumentRow
      label={label}
      name={name as string}
      value={(formData?.[name] as string) || ''}
      isEditable={isEditable}
      onChange={handleChange}
    />
  );

  const renderDatePicker = (name: keyof PLOFormOneData) => (
    <FormDatePicker
      name={name as string}
      value={(formData?.[name] as string) || ''}
      isEditable={isEditable}
      onChange={handleChange}
      onClear={handleClearDate}
    />
  );

  const attachments = [
    { name: 'ရဲစခန်း ပထမသတင်းပေးတိုင်ချက်.pdf', size: '1.2 MB' },
    { name: 'သက်သေထွက်ဆိုချက်_အကျဉ်း.docx', size: '450 KB' },
  ];

  const renderStageProgression = () => (
    <StageProgression
      status={formData.status}
      error={error}
      onErrorDismiss={() => setError(null)}
      targetGeoLevels={formData.targetGeoLevels}
      originOfficeName={formData.originOffice?.name}
      originGeoLevel={'originGeoLevel' in formData ? formData.originGeoLevel : undefined}
      auditLogs={formData.auditLogs}
      stageHistory={formData.stageHistory}
    />
  );

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 font-sans bg-slate-50 min-h-screen space-y-6">
      {/* Top Header Navigation Bar */}
      <DetailHeader
        title="အကြိုတွေ့ဆုံမေးမြန်းခြင်း ပြီးပြတ်ချက် အသေးစိတ် စိစစ်ခြင်း"
        caseNumber={formData.caseNumber}
        isEditable={isEditable}
        saving={saving}
        onBack={handleBackNavigation}
        // onSave={handleSaveAction}
      />

      {renderStageProgression()}

      {/* Main Grid Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT SECTION: Document View (8 Columns) */}
        <div className="lg:col-span-8">
          <PloDetailFormOneDocument
            formData={formData}
            isEditable={isEditable}
            isSigning={isSigning}
            sigCanvasRef={sigCanvasRef}
            setIsSigning={setIsSigning}
            handleChange={handleChange}
            handleClearSignature={handleClearSignature}
            handleSaveSignature={handleSaveSignature}
            renderDocumentRow={renderDocumentRow}
            renderDatePicker={renderDatePicker} isPreview={false}          />
        </div>

        {/* RIGHT SECTION: Action Sidebar (4 Columns) */}
        <div className="lg:col-span-4">
          {/* <ActionSidebar
            formData={formData}
            saving={saving}
            remark={remark}
            setRemark={setRemark}
            attachments={attachments}
            onReturn={handleRejectAction}
            onReject={handleRejectAction}
            onApprove={handleApproveAction}
            onStartReview={handleStartReviewAction}
          /> */}
          <ActionSidebar
                      formData={formData}
                      saving={saving}
                      remark={remark}
                      setRemark={setRemark}
                      attachments={attachments}
                      onFileChange={handleFileChange}
                      onRemoveFile={handleRemoveFile}
                      onSave={handleAttachmentAction}
                      onStartReview={handleStartReviewAction}
                    />
        </div>
      </div>
    </div>
  );
}